// src/utils/phoneFormatter.ts

/**
 * Formata um número de telefone brasileiro
 * @param value String com dígitos
 * @returns String formatada no padrão brasileiro
 */
export const formatPhoneNumber = (value: string): string => {
  // Remove todos os caracteres que não são dígitos
  const digitsOnly = value.replace(/\D/g, '');
  
  // Limita a 11 dígitos (celular) ou 10 dígitos (fixo)
  const limited = digitsOnly.slice(0, 11);
  
  // Aplica a formatação baseada no número de dígitos
  if (limited.length <= 2) {
    return limited;
  } else if (limited.length <= 6) {
    return `(${limited.slice(0, 2)}) ${limited.slice(2)}`;
  } else if (limited.length <= 10) {
    return `(${limited.slice(0, 2)}) ${limited.slice(2, 6)}-${limited.slice(6)}`;
  } else {
    // Celular com 11 dígitos: (XX) 9XXXX-XXXX
    return `(${limited.slice(0, 2)}) ${limited.slice(2, 7)}-${limited.slice(7)}`;
  }
};

/**
 * Remove formatação do telefone, deixando apenas dígitos
 * @param formattedPhone Telefone formatado
 * @returns String com apenas dígitos
 */
export const removePhoneFormatting = (formattedPhone: string): string => {
  return formattedPhone.replace(/\D/g, '');
};

/**
 * Valida se o número de telefone brasileiro está no formato correto
 * @param phone Número de telefone
 * @returns true se válido, false caso contrário
 */
export const isValidBrazilianPhone = (phone: string): boolean => {
  const digits = removePhoneFormatting(phone);
  
  // Deve ter 10 dígitos (fixo) ou 11 dígitos (celular)
  if (digits.length !== 10 && digits.length !== 11) {
    return false;
  }
  
  // Verifica se é um DDD válido (11-99)
  const ddd = parseInt(digits.slice(0, 2));
  if (ddd < 11 || ddd > 99) {
    return false;
  }
  
  // Para celular (11 dígitos), o terceiro dígito deve ser 9
  if (digits.length === 11 && digits[2] !== '9') {
    return false;
  }
  
  // Para fixo (10 dígitos), o terceiro dígito não deve ser 9
  if (digits.length === 10 && digits[2] === '9') {
    return false;
  }
  
  return true;
};

/**
 * Placeholder dinâmico baseado no que foi digitado
 * @param value Valor atual do campo
 * @returns Placeholder apropriado
 */
export const getPhonePlaceholder = (value: string): string => {
  const digits = removePhoneFormatting(value);
  
  if (digits.length === 0) {
    return '(11) 99999-9999';
  } else if (digits.length <= 2) {
    return '(11) 99999-9999';
  } else if (digits.length === 3 && digits[2] === '9') {
    return '(11) 99999-9999'; // Celular
  } else if (digits.length === 3 && digits[2] !== '9') {
    return '(11) 3333-4444'; // Fixo
  } else if (digits.length > 3 && digits[2] === '9') {
    return '(11) 99999-9999'; // Celular
  } else {
    return '(11) 3333-4444'; // Fixo
  }
};